import { CostOfLivingPayload } from "../payloadTypes";
import { CostOfLivingData, FactRow } from "../types";
import { withHeroImageParams } from "./neighbourhoodHelpers";
import config from "@Config/index";

const heroImage = withHeroImageParams(
  `${config.IMAGE_STATIC_URL}/neighbourhood-assets/cost-of-living.png`,
);
const KEY_PREFIX = "propertypage.components.neighbourhood.costOfLiving";

/**
 * Transforms the backend cost_of_living payload into a CostOfLivingData view model.
 *
 * Hero: rent price_percent_difference → "{x}% higher/lower compared to {country} average"
 * Fact rows (fixed order): rent, transportation, groceries.
 * - Hide any row with missing or zero data — never show 0% or "N/A".
 * - Tab rendered only if 2+ rows have data (handled by NeighbourhoodSection).
 * Source: "Numbeo data"
 */
export const transformCostOfLiving = (
  costOfLiving: CostOfLivingPayload | null | undefined,
  countryName: string | undefined,
  rentBudgetPercent?: number | null,
  cityName?: string,
): CostOfLivingData => {
  const facts: FactRow[] = [];

  // --- Rent row ---
  // Prefer the Price Trend "budget vs {city} average" figure when available.
  // There negative = cheaper, positive = higher (compared to the city average).
  const hasBudgetPercent =
    rentBudgetPercent != null &&
    Number.isFinite(rentBudgetPercent) &&
    Math.abs(Math.round(rentBudgetPercent)) > 0 &&
    !!cityName;

  if (hasBudgetPercent) {
    const absVal = Math.abs(Math.round(rentBudgetPercent as number));
    if ((rentBudgetPercent as number) > 0) {
      facts.push({
        key: `${KEY_PREFIX}.fact.rent.cityHigher`,
        params: { percent: String(absVal), city: cityName as string },
        trend: "up",
      });
    } else {
      facts.push({
        key: `${KEY_PREFIX}.fact.rent.cityCheaper`,
        params: { percent: String(absVal), city: cityName as string },
        trend: "down",
      });
    }
  } else {
    // Fallback: Numbeo rent price_percent_difference vs the top-cities average.
    const rentValue = costOfLiving?.rent?.price_percent_difference;
    if (rentValue != null && Number.isFinite(rentValue) && rentValue !== 0) {
      const absVal = Math.abs(Math.round(rentValue));
      if (absVal > 0) {
        if (rentValue > 0) {
          facts.push({
            key: `${KEY_PREFIX}.fact.rent.higher`,
            params: { percent: String(absVal) },
            trend: "up",
          });
        } else {
          facts.push({
            key: `${KEY_PREFIX}.fact.rent.cheaper`,
            params: { percent: String(absVal) },
            trend: "down",
          });
        }
      }
    }
  }

  // --- Transportation row ---
  const transportValue = costOfLiving?.transportation?.price_percent_difference;
  if (transportValue != null && Number.isFinite(transportValue) && transportValue !== 0) {
    const absVal = Math.abs(Math.round(transportValue));
    if (absVal > 0) {
      if (transportValue > 0) {
        facts.push({
          key: `${KEY_PREFIX}.fact.transport.higher`,
          params: { percent: String(absVal) },
          trend: "up",
        });
      } else {
        facts.push({
          key: `${KEY_PREFIX}.fact.transport.lower`,
          params: { percent: String(absVal) },
          trend: "down",
        });
      }
    }
  }

  // --- Groceries row ---
  const groceriesValue = costOfLiving?.groceries?.price_percent_difference;
  if (groceriesValue != null && Number.isFinite(groceriesValue) && groceriesValue !== 0) {
    const absVal = Math.abs(Math.round(groceriesValue));
    if (absVal > 0) {
      if (groceriesValue > 0) {
        facts.push({
          key: `${KEY_PREFIX}.fact.groceries.higher`,
          params: { percent: String(absVal) },
          trend: "up",
        });
      } else {
        facts.push({
          key: `${KEY_PREFIX}.fact.groceries.cheaper`,
          params: { percent: String(absVal) },
          trend: "down",
        });
      }
    }
  }

  // --- Hero statistic (derived from rent) ---
  // Mirror the rent-row source: prefer the Price Trend budget %, else Numbeo rent.
  let percentageDiff: string | undefined;
  let direction: "higher" | "lower" | undefined;

  const heroRentValue = hasBudgetPercent
    ? (rentBudgetPercent as number)
    : costOfLiving?.rent?.price_percent_difference;

  if (heroRentValue != null && Number.isFinite(heroRentValue) && heroRentValue !== 0) {
    const absVal = Math.abs(Math.round(heroRentValue));
    if (absVal > 0) {
      percentageDiff = `${absVal}%`;
      direction = heroRentValue > 0 ? "higher" : "lower";
    }
  }

  // Hero label mirrors the rent-row source: "{city} average" when the Price Trend
  // budget % drives it, otherwise the generic "top cities average".
  const comparisonLabelKey = hasBudgetPercent
    ? `${KEY_PREFIX}.hero.label.city`
    : `${KEY_PREFIX}.hero.label`;

  return {
    heroImage,
    ...(percentageDiff !== undefined && { percentageDiff }),
    ...(direction !== undefined && { direction }),
    comparisonLabelKey,
    ...(hasBudgetPercent && { comparisonLabelParams: { city: cityName as string } }),
    countryName,
    facts,
    sourceKey: `${KEY_PREFIX}.source`,
  };
};

export default transformCostOfLiving;
